// crateWeightSamples — pure rules for the periodic crate-weight SAMPLES (mig
// 20261192500000). QC weighs ~10 crates of a cultivar every few days; every
// delivery of that cultivar then ESTIMATES its kg from the newest sample on or
// before its date, until a newer one is entered.
//
// An estimate is not a measurement: callers show it marked "est." and keep it
// out of the measured totals. Nothing here is ever written to delivery_weighings.

export interface CrateWeightSample {
  sample_id:           string
  variety:             string
  crate_type:          string | null
  /** YYYY-MM-DD */
  sampled_on:          string
  crates_weighed:      number
  gross_kg:            number
  avg_gross_per_crate: number
  note:                string | null
  created_at:          string | null
  deleted_at:          string | null
}

/** True when `a` is the newer sample: later date, then later created_at (same-day ties). */
function isNewer(a: CrateWeightSample, b: CrateWeightSample): boolean {
  if (a.sampled_on !== b.sampled_on) return a.sampled_on > b.sampled_on
  return (a.created_at ?? '') > (b.created_at ?? '')
}

/** Per-crate tare for a crate type, or null when none is configured (> 0). Never a placeholder. */
export function configuredTare(tares: Record<string, number>, crateType: string | null | undefined): number | null {
  const t = tares[crateType ?? 'BLUEBERRY_CRATE']
  return t != null && t > 0 ? t : null
}

const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()

/** The newest live sample for `variety` taken on or before `onDate` (YYYY-MM-DD), else null. */
export function sampleFor(
  samples: readonly CrateWeightSample[], variety: string | null | undefined, onDate: string,
): CrateWeightSample | null {
  const v = norm(variety)
  if (!v) return null
  let best: CrateWeightSample | null = null
  for (const s of samples) {
    if (s.deleted_at || norm(s.variety) !== v || s.sampled_on > onDate) continue
    if (!best || isNewer(s, best)) best = s
  }
  return best
}

/** Average gross kg per crate from the newest applicable sample, else null. */
export function sampleAvgFor(
  samples: readonly CrateWeightSample[], variety: string | null | undefined, onDate: string,
): number | null {
  const avg = Number(sampleFor(samples, variety, onDate)?.avg_gross_per_crate)
  return avg > 0 ? avg : null
}

/**
 * Estimated net kg for `crates` crates: crates × (sample avg gross − tare).
 * Null when there is no sample or the sample does not clear the tare.
 */
export function estimateNetKg(crates: number, avgGross: number | null, tareKg: number): number | null {
  if (avgGross == null || !(crates > 0)) return null
  const perCrate = avgGross - tareKg
  return perCrate > 0 ? crates * perCrate : null
}

/** Latest live sample per variety (case-insensitive), newest first by date. */
export function latestPerVariety(samples: readonly CrateWeightSample[]): CrateWeightSample[] {
  const m = new Map<string, CrateWeightSample>()
  for (const s of samples) {
    if (s.deleted_at) continue
    const k = norm(s.variety)
    const cur = m.get(k)
    if (!cur || isNewer(s, cur)) m.set(k, s)
  }
  return [...m.values()].sort((a, b) => b.sampled_on.localeCompare(a.sampled_on) || a.variety.localeCompare(b.variety))
}

export interface EstimateInput {
  /** Crates received (bins_received on a berry delivery). */
  crates:    number
  variety:   string | null | undefined
  /** YYYY-MM-DD the delivery arrived (SAST). */
  onDate:    string
  /** Measured net kg from weighings, 0/undefined when none. */
  measuredNetKg?: number
}

/**
 * A delivery's ESTIMATED net kg, only when it has no measured weighing:
 * crates x (newest sample's avg gross - crate tare). Null otherwise.
 */
export function estimateDeliveryNetKg(
  d: EstimateInput, samples: readonly CrateWeightSample[], tareKg: number | null,
): number | null {
  // No configured tare = no estimate (a placeholder tare would invent a figure).
  if (tareKg == null || (d.measuredNetKg ?? 0) > 0) return null
  return estimateNetKg(d.crates, sampleAvgFor(samples, d.variety, d.onDate), tareKg)
}

/** Who may soft-delete a sample: the ACTIVE tenant's MANAGER/ADMIN/SUPER_ADMIN role, or the platform tier. */
export function canDeleteSample(tenantRole: string | null | undefined, globalRole: string | null | undefined): boolean {
  return tenantRole === 'MANAGER' || tenantRole === 'ADMIN' || tenantRole === 'SUPER_ADMIN' || globalRole === 'SUPER_ADMIN'
}

/** CSV text for a delivery left out of the estimate because no crate tare is set. */
export const TARE_NOT_SET = 'tare not set'

/** Estimated-total text: an em dash (never 0) when no delivery could be estimated. */
export function estTotalLabel(total: number, estimated: number, digits = 0): string {
  return estimated > 0 ? total.toFixed(digits) : '—'
}

/** "N deliveries left out: crate tare not set", or null when none were. */
export function leftOutNote(n: number): string | null {
  return n > 0 ? `${n} ${n === 1 ? 'delivery' : 'deliveries'} left out: crate tare not set` : null
}
